/**
 * OneTrust (CookiePro) helpers: banner language / UI, and Google Consent Mode via `gtag`.
 * Map cookie groups to gtag: **C0002** → `analytics_storage`, **C0004** → `ad_storage` (+ related).
 * Supports legacy active-group lists (`…,C0002,…`) and explicit flags (`C0002:0` / `C0002:1`).
 * US region: CookiePro decides whether to show a notice; we sync `OptanonActiveGroups` to gtag and
 * third-party injection (no blanket auto-grant). Non-US: same sync, plus preference UI behavior below.
 *
 * @see https://developer.onetrust.com/onetrust/docs/single-page-applications
 * @see https://developers.google.com/tag-platform/security/guides/consent
 */

import { Language, StoreId } from "@ubnt/store.core/lib/types";

import {
  injectThirdPartyTagsForGtagConsent,
  resetDeferredThirdPartyInjectionState,
} from "./injectThirdPartyTagsForConsent";
import {
  clearGoogleAnalyticsCookies,
  clearHotjarCookies,
  clearRedditPixelCookie,
} from "./clearFirstPartyConsentCookies";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    OnetrustActiveGroups?: string;
    OptanonActiveGroups?: string;
    OneTrust?: {
      ToggleInfoDisplay?: (forceShow?: boolean) => void;
      changeLanguage?: (language: string) => void;
      OnConsentChanged?: (callback: () => void) => void;
    };
  }
}

const getLanguageCode = (language?: string): string => {
  const lowercasedLang = language?.toLowerCase() ?? Language.En;
  if (lowercasedLang.includes("-")) {
    // @NOTE Extract primary language code (Taiwanese not supported yet)
    return lowercasedLang.split("-")[0];
  }
  return lowercasedLang;
};

/**
 * Syncs OneTrust language only (does not open the preference center). Use for `StoreId.Us` so
 * CookiePro controls whether the banner appears.
 */
export const syncOneTrustLanguageOnly = (language?: string): void => {
  if (!window.OneTrust) {
    return;
  }

  window.OneTrust.changeLanguage?.(getLanguageCode(language));
};

/**
 * Syncs OneTrust language and surfaces the preferences UI (non-US flows).
 * Used on language change and on initial load for non-`StoreId.Us` stores.
 */
export const handleOneTrust = (language?: string): void => {
  if (!window.OneTrust) {
    return;
  }

  syncOneTrustLanguageOnly(language);

  setTimeout(() => {
    window.OneTrust?.ToggleInfoDisplay?.(true);
  }, 1000);
};

// --- Google Consent Mode (gtag) + OneTrust active groups ---

let onConsentHooked = false;

const isGtagReady = (): boolean => {
  return typeof window.gtag === "function";
};

/** GA4 measurement ID — keep in sync with CookiePro / GTM tenant config. */
const GA4_MEASUREMENT_ID = "G-V01X6F3X1P";

let ga4LibraryRequested = false;

const resetGa4DeferredLoadState = (): void => {
  ga4LibraryRequested = false;
};

/**
 * Loads `gtag/js` and runs `config` only after `analytics_storage` is granted,
 * so GA4 does not set `_ga` cookies on non-consented GDPR flows.
 */
const ensureGa4LibraryAfterAnalyticsGranted = (): void => {
  if (ga4LibraryRequested) {
    return;
  }
  if (!isGtagReady()) {
    return;
  }
  ga4LibraryRequested = true;
  const src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_MEASUREMENT_ID)}`;
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    window.gtag!("js", new Date());
    window.gtag!("config", GA4_MEASUREMENT_ID);
    return;
  }
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  s.onload = () => {
    window.gtag!("js", new Date());
    window.gtag!("config", GA4_MEASUREMENT_ID);
  };
  document.head.appendChild(s);
};

const pushGtagConsentUpdate = (params: Record<string, string>): void => {
  if (!isGtagReady()) {
    return;
  }
  window.gtag!("consent", "update", params);
  injectThirdPartyTagsForGtagConsent(params);
  if (params.analytics_storage === "granted") {
    ensureGa4LibraryAfterAnalyticsGranted();
  } else {
    resetGa4DeferredLoadState();
    clearGoogleAnalyticsCookies();
    clearHotjarCookies();
  }
  if (params.ad_storage !== "granted") {
    clearRedditPixelCookie();
  }
  if (
    params.analytics_storage !== "granted" &&
    params.ad_storage !== "granted"
  ) {
    resetDeferredThirdPartyInjectionState();
  }
};

/** OneTrust exposes accepted cookie groups on `window` (name varies by SDK). */
const readActiveGroupsCsv = (): string => {
  if (typeof window.OnetrustActiveGroups === "string") {
    return window.OnetrustActiveGroups;
  }
  if (typeof window.OptanonActiveGroups === "string") {
    return window.OptanonActiveGroups;
  }
  return "";
};

/** Literal patterns only — satisfies `security/detect-non-literal-regexp`. */
const ONETRUST_GROUP_FLAG_RE: Record<"C0002" | "C0004", RegExp> = {
  C0002: /C0002:\s*([01])/i,
  C0004: /C0004:\s*([01])/i,
};

/**
 * OneTrust may expose either a legacy list of **active** IDs (`C0002,C0004`) or explicit
 * flags (`C0002:1`, `C0002:0`). The latter must be parsed; `,C0002,` does not appear in `C0002:0`.
 */
const parseOneTrustGroupOn = (
  csv: string,
  groupId: keyof typeof ONETRUST_GROUP_FLAG_RE,
): boolean | null => {
  const matchedRegEx = csv.match(ONETRUST_GROUP_FLAG_RE[groupId]);
  if (matchedRegEx) {
    return matchedRegEx[1] === "1";
  }
  return null;
};

const consentFromGroupsCsv = (csv: string): Record<string, string> => {
  const compact = csv.replace(/\s/g, "");
  const explicitA = parseOneTrustGroupOn(compact, "C0002");
  const explicitAds = parseOneTrustGroupOn(compact, "C0004");
  const g = `,${compact},`;
  const analytics =
    explicitA !== null ? explicitA : g.includes(",C0002,");
  const ads = explicitAds !== null ? explicitAds : g.includes(",C0004,");
  const adState = ads ? "granted" : "denied";
  return {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: adState,
    ad_user_data: adState,
    ad_personalization: adState,
  };
};

export const pushGtagConsentUpdateFromOneTrustGroups = (): void => {
  pushGtagConsentUpdate(consentFromGroupsCsv(readActiveGroupsCsv()));
};

const hookOnConsentChanged = (): boolean => {
  if (onConsentHooked) {
    return true;
  }
  const consentCallback = window.OneTrust?.OnConsentChanged;
  if (typeof consentCallback !== "function") {
    return false;
  }
  onConsentHooked = true;
  consentCallback(() => {
    window.setTimeout(() => {
      pushGtagConsentUpdateFromOneTrustGroups();
    }, 0);
  });
  return true;
};

/** Registers OneTrust → gtag listener (once) and pushes current active groups to `gtag`. */
const syncOneTrustGroupsToGtag = (): boolean => {
  const hooked = hookOnConsentChanged();
  pushGtagConsentUpdateFromOneTrustGroups();
  return hooked;
};

/** After language change (or any time you need a fresh gtag sync). */
export const refreshOneTrustGtag = (
  storeId: StoreId,
  language: string,
): void => {
  if (storeId === StoreId.Us) {
    syncOneTrustLanguageOnly(language);
  } else {
    handleOneTrust(language);
  }
  syncOneTrustGroupsToGtag();
};

const POLL_MS = 100;
const MAX_TICKS = 40;
const SYNC_STOP_AFTER_TICKS = 15;

/**
 * Polls until OneTrust / gtag exist, then wires OneTrust active groups → gtag + `OnConsentChanged`.
 * US: language only (no forced preference modal); non-US: also opens preference UI once (legacy).
 */
export const startOneTrustGtagSync = (
  storeId: StoreId,
  language: string,
): (() => void) => {
  let ticks = 0;
  let ranInitialOneTrustUi = false;
  const isUsStore = storeId === StoreId.Us;

  const id = window.setInterval(() => {
    ticks += 1;
    if (ticks > MAX_TICKS) {
      window.clearInterval(id);
      return;
    }

    if (!window.OneTrust || !isGtagReady()) {
      return;
    }

    if (!ranInitialOneTrustUi) {
      ranInitialOneTrustUi = true;
      if (isUsStore) {
        syncOneTrustLanguageOnly(language);
      } else {
        handleOneTrust(language);
      }
    }

    const hooked = syncOneTrustGroupsToGtag();
    if (hooked || ticks >= SYNC_STOP_AFTER_TICKS) {
      window.clearInterval(id);
    }
  }, POLL_MS);

  return () => {
    window.clearInterval(id);
  };
};

export default handleOneTrust;
